import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "checkbox", "submitButton", "total"]
  selectedServices = new Set()

  connect() {
    // Initialize selectedServices from pre-checked checkboxes
    this.cardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox && checkbox.checked) {
        this.selectedServices.add(card.dataset.serviceId)
      }
    })

    // Listen for price changes from service-interval controller
    this.element.addEventListener("priceChanged", this.handlePriceChange.bind(this))

    this.updateTotal()
    this.updateSubmitButton()
    this.updateCardStates()
  }

  disconnect() {
    this.element.removeEventListener("priceChanged", this.handlePriceChange.bind(this))
  }

  handlePriceChange(event) {
    // Re-calculate the total when a billing option changes
    this.updateTotal()
  }

  toggleService(event) {
    const card = event.currentTarget
    const checkbox = card.querySelector('input[type="checkbox"]')
    const serviceId = card.dataset.serviceId
    const isQuoteRequest = card.dataset.quoteRequest === 'true'

    // Don't allow interaction with disabled cards
    if (card.classList.contains('pointer-events-none')) {
      return
    }

    // Toggle checkbox if clicking card (but not if clicking checkbox directly)
    if (event.target !== checkbox) {
      checkbox.checked = !checkbox.checked
    }

    // Update selected services set and card styling
    if (checkbox.checked) {
      if (isQuoteRequest) {
        // Deselect all other services when quote request is selected
        this.deselectAllExcept(serviceId)
      } else {
        // Deselect any quote request service when regular service is selected
        this.deselectQuoteRequestServices()
      }
      this.selectedServices.add(serviceId)
      this.selectCard(card)
    } else {
      this.selectedServices.delete(serviceId)
      this.deselectCard(card)
    }

    this.updateTotal()
    this.updateSubmitButton()
    this.updateCardStates()
  }

  deselectAllExcept(keepServiceId) {
    this.cardTargets.forEach(card => {
      if (card.dataset.serviceId !== keepServiceId) {
        const checkbox = card.querySelector('input[type="checkbox"]')
        if (checkbox && checkbox.checked) {
          checkbox.checked = false
          this.selectedServices.delete(card.dataset.serviceId)
          this.deselectCard(card)
        }
      }
    })
  }

  deselectQuoteRequestServices() {
    this.cardTargets.forEach(card => {
      if (card.dataset.quoteRequest === 'true') {
        const checkbox = card.querySelector('input[type="checkbox"]')
        if (checkbox && checkbox.checked) {
          checkbox.checked = false
          this.selectedServices.delete(card.dataset.serviceId)
          this.deselectCard(card)
        }
      }
    })
  }

  selectCard(card) {
    const isQuoteRequest = card.dataset.quoteRequest === 'true'
    const variant = card.dataset.selectedVariant || 'selected'
    // Use CSS class-based selection (styles in cards.css .selectable-card)
    if (isQuoteRequest) {
      card.classList.add('selected-amber')
    } else {
      card.classList.add(variant)
    }
  }

  deselectCard(card) {
    const isQuoteRequest = card.dataset.quoteRequest === 'true'
    const variant = card.dataset.selectedVariant || 'selected'
    if (isQuoteRequest) {
      card.classList.remove('selected-amber')
    } else {
      card.classList.remove(variant)
    }
  }

  updateCardStates() {
    // Check if a quote request service is selected
    const quoteRequestSelected = this.cardTargets.some(card => {
      return card.dataset.quoteRequest === 'true' && this.selectedServices.has(card.dataset.serviceId)
    })

    // Check if any regular service is selected
    const regularServiceSelected = this.cardTargets.some(card => {
      return card.dataset.quoteRequest !== 'true' && this.selectedServices.has(card.dataset.serviceId)
    })

    this.cardTargets.forEach(card => {
      const isQuoteRequest = card.dataset.quoteRequest === 'true'
      const isSelected = this.selectedServices.has(card.dataset.serviceId)

      if (quoteRequestSelected && !isQuoteRequest) {
        // Quote request is selected - disable regular services
        this.disableCard(card)
      } else if (regularServiceSelected && isQuoteRequest) {
        // Regular service is selected - disable quote request services
        this.disableCard(card)
      } else {
        // No conflict - enable the card
        this.enableCard(card)
      }
    })
  }

  disableCard(card) {
    card.classList.add('disabled')
    const checkbox = card.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.disabled = true
    }
  }

  enableCard(card) {
    card.classList.remove('disabled')
    const checkbox = card.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.disabled = false
    }
  }

  updateTotal() {
    let total = 0
    this.cardTargets.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      if (checkbox && checkbox.checked) {
        const price = parseFloat(card.dataset.price)
        total += price
      }
    })

    // Update total in bottom bar (outside controller scope, so find by ID)
    const totalElement = document.getElementById('services-total')
    if (totalElement) {
      totalElement.textContent = this.formatCurrency(total)
    }
  }

  updateSubmitButton() {
    const isDisabled = this.selectedServices.size === 0

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isDisabled
    }

    // Also sync the visible button in bottom bar
    const visibleBtn = document.getElementById('visible-services-submit-btn')
    if (visibleBtn) {
      visibleBtn.disabled = isDisabled
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }
}
